import React, { useCallback } from 'react';
import styled from 'styled-components';
import CloseBtn from '@components/dataEntry/button/CloseBtn/CloseBtn';
import TimeAgo from '@components/dataDisplay/TimeAgo';
import QuillContents from '@template/QuillContents/QuillContents';

/**
 * 재사용 가능한 인앱 알림 토스트 카드.
 *
 * - 제목 / 내용 / 시간 3요소만 노출 (아바타 없음).
 * - persistent 노출 전제, 타이머 없음.
 * - body는 plain text(`body`) 또는 HTML(`bodyHtml`) 중 하나를 받는다.
 *   - HTML은 QuillContents로 렌더링(포스팅과 동일한 규격).
 *   - 2줄 초과 시 `...` 말줄임 처리.
 */

const CardSec = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 260px;
  max-width: 320px;
  text-align: left;
  cursor: pointer;

  @media (max-width: 480px) {
    width: 100%;
    min-width: 0;
    max-width: none;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const Title = styled.strong`
  font-size: 13px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CountBadge = styled.span`
  flex: 0 0 auto;
  font-size: 11px;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 10px;
  background: var(--inkColor);
  color: var(--subTextColor);
`;

const BodyText = styled.p`
  font-size: 13px;
  color: var(--subTextColor);
  line-height: 1.4;
  max-height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
`;

/**
 * HTML 본문(QuillContents)을 2줄 고정 말줄임 처리하기 위한 래퍼.
 * - QuillContents 내부 블록 요소를 inline으로 평탄화하여 한 블록처럼 line-clamp.
 * - 이미지/미디어/임베드는 toast 프리뷰에 과하므로 숨김.
 */
const BodyHtmlClamp = styled.div`
  font-size: 13px;
  color: var(--subTextColor);
  line-height: 1.4;
  max-height: 40px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;

  & p,
  & div,
  & span,
  & li,
  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6,
  & blockquote {
    display: inline;
    margin: 0;
    padding: 0;
    font-size: inherit;
    color: inherit;
    line-height: inherit;
  }
  & br {
    display: none;
  }
  & img,
  & video,
  & iframe,
  & figure,
  & .ql-custom-emoji-thumbnail {
    display: none !important;
  }
  & ul,
  & ol {
    display: inline;
    margin: 0;
    padding: 0;
    list-style: none;
  }
`;

const MetaRow = styled.div`
  font-size: 11px;
  color: var(--subTextColor);
  opacity: 0.8;
`;

export type NotificationToastCardProps = {
  title: string;
  /** 타이틀 앞에 붙는 prefix (예: "[No.76] ") */
  titlePrefix?: string;
  /** 평문 본문 (bodyHtml이 없을 때 사용) */
  body?: string;
  /** HTML 본문 (댓글/답글/방명록 등 에디터 원본) */
  bodyHtml?: string;
  /** body 앞에 붙이는 발신자 prefix (예: "무공: ") */
  bodyPrefix?: string;
  createdAt?: any;
  count?: number;
  onClick?: () => void;
  onClose?: (e: React.MouseEvent) => void;
};

function NotificationToastCard({
  title,
  titlePrefix,
  body,
  bodyHtml,
  bodyPrefix,
  createdAt,
  count = 1,
  onClick,
  onClose,
}: NotificationToastCardProps) {
  const handleCardClick = useCallback(() => {
    onClick?.();
  }, [onClick]);

  const handleClose = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onClose?.(e);
    },
    [onClose],
  );

  return (
    <CardSec
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleCardClick();
      }}
    >
      <HeaderRow>
        <TitleRow>
          <Title>
            {titlePrefix}
            {title}
          </Title>
          {count > 1 && <CountBadge>+{count - 1}</CountBadge>}
        </TitleRow>
        {onClose && (
          <CloseBtn onClick={handleClose} shape="stroke" size="sm" />
        )}
      </HeaderRow>

      {bodyHtml ? (
        <BodyHtmlClamp>
          {bodyPrefix ? <span>{bodyPrefix}</span> : null}
          <QuillContents value={bodyHtml} lineHeight={1.4} />
        </BodyHtmlClamp>
      ) : body ? (
        <BodyText>
          {bodyPrefix ? <span>{bodyPrefix}</span> : null}
          {body}
        </BodyText>
      ) : null}

      {createdAt && (
        <MetaRow>
          <TimeAgo createdAt={createdAt} />
        </MetaRow>
      )}
    </CardSec>
  );
}

export default React.memo(NotificationToastCard);
